import SelectDamagedPartGroup from 'components/elements/form/DamagedPartGroup/SelectDamagedPartGroup'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import { WARNING } from 'constants/messages'
import RequestTemplate from 'components/templates/RequestTemplate'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import { ERROR } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React, { useEffect } from 'react'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { containsIncompatibleChar } from 'utils/commonUtils'
import {
  AccidentImage,
  AccidentImageKeys,
  AccidentPartInfo,
  DestinationTypes,
  FieldTypes,
  FallingWheelStatusTypes,
  ImageInfo,
  QnStatesProps,
  initialQnEditState,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import { OptionItem } from 'components/elements/common'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { isSjDomain, isKsDomain, isSaficDomain, getDomain } from 'utils/domain'
import { success } from 'utils/status'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import { createOptions } from 'utils/commonUtils'
import DOMAIN from 'constants/domains'

const fallingWheelStatusOptions = createOptions(
  VEHICLE_STATE_FORM.fallingWheelStatus.options
)

/**
 * QN-03-f: 落輪・乗り上げ
 */
const FallingWheel: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const {
    accidentImage,
    accidentPart,
    noPhoto,
    destinationType,
    fallingWheelStatus,
    fieldType,
    isNotApplicableParts,
    isMotorbike,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  const errorMessage = React.useMemo(() => {
    if (fieldType.type.includes(FieldTypes.others)) {
      return containsIncompatibleChar(fieldType.others)
        ? ERROR.CONTAINS_SURROGATE_PAIR
        : ''
    }
    return ''
  }, [editState.vehicleStateInfo])

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const hasNoDamagedParts = accidentPart.parts.length <= 0
  const hasNoPicture =
    !accidentImage.damagedPart.image || !accidentImage.peripheral.image
  const inputCheck = noPhoto
    ? hasNoDamagedParts && !isNotApplicableParts
    : hasNoPicture

  /** 決定ボタンの活性状態フラグ */
  const isDisabled = React.useMemo(() => {
    let isDisable = false
    if (destinationType.includes(DestinationTypes.specify)) {
      isDisable =
        (fieldType.type.includes(FieldTypes.others)
          ? Boolean(errorMessage) || inputCheck
          : inputCheck) || !editState.destinationLocation.isSelected
    }

    if (!destinationType.includes(DestinationTypes.specify)) {
      isDisable = fieldType.type.includes(FieldTypes.others)
        ? Boolean(errorMessage) || inputCheck
        : inputCheck
    }
    if (
      destinationType.includes(DestinationTypes.specify) &&
      editState.destinationLocation.isSelected
    ) {
      isDisable = addressMatchError
    }
    return isDisable
  }, [
    editState.vehicleStateInfo,
    editState.destinationLocation,
    addressMatchError,
  ])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    switch (destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
    return ''
  }, [destinationType])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  //現場の種類の選択肢
  const fieldTypeOptions = React.useMemo(() => {
    const choseFieldTypes: Record<
      FallingWheelStatusTypes,
      Array<OptionItem<FieldTypes>>
    > = {
      [FallingWheelStatusTypes.falling]: [
        {
          label: VEHICLE_STATE_FORM.fieldType.options.gutter,
          value: FieldTypes.gutter,
        },
        {
          label: VEHICLE_STATE_FORM.fieldType.options.others,
          value: FieldTypes.others,
        },
      ],
      [FallingWheelStatusTypes.rideup]: [
        {
          label: VEHICLE_STATE_FORM.fieldType.options.curb,
          value: FieldTypes.curb,
        },
        {
          label: VEHICLE_STATE_FORM.fieldType.options.others,
          value: FieldTypes.others,
        },
      ],
      [FallingWheelStatusTypes['']]: [
        {
          label: '',
          value: FieldTypes.gutter,
        },
        {
          label: '',
          value: FieldTypes.others,
        },
      ],
    }

    return choseFieldTypes[fallingWheelStatus]
  }, [fallingWheelStatus])

  //現場の種類の初期値
  useEffect(() => {
    setVehicleStateInfo({
      fieldType: {
        ...fieldType,
        type: fieldType.type.includes(FieldTypes.others)
          ? FieldTypes.others
          : fallingWheelStatus == FallingWheelStatusTypes.falling
          ? FieldTypes.gutter
          : FieldTypes.curb,
      },
    })
  }, [fallingWheelStatus])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      fallingWheelStatus:
        fallingWheelStatus === ''
          ? FallingWheelStatusTypes.falling
          : fallingWheelStatus,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  //落輪or乗り上げのタイトル名変更処理
  const damagedAreaTitle = React.useMemo((): string => {
    return fallingWheelStatus.includes(FallingWheelStatusTypes.falling)
      ? VEHICLE_STATE_FORM.fallingWheelPart.formName
      : VEHICLE_STATE_FORM.rideupWheelPart.formName
  }, [fallingWheelStatus])

  //バイクの落輪or乗り上げのタイトル名変更処理
  const motorbikeTitle = React.useMemo((): string => {
    return fallingWheelStatus.includes(FallingWheelStatusTypes.falling)
      ? VEHICLE_STATE_FORM.motorbikeFallingWheel.formName
      : VEHICLE_STATE_FORM.motorbikeRideup.formName
  }, [fallingWheelStatus])

  //バイクの落輪or乗り上げのラベル名変更処理
  const motorbikeLabel = React.useMemo((): string => {
    return fallingWheelStatus.includes(FallingWheelStatusTypes.falling)
      ? VEHICLE_STATE_FORM.motorbikeFallingWheel.label
      : VEHICLE_STATE_FORM.motorbikeRideup.label
  }, [fallingWheelStatus])

  /** 画像選択変更時のコールバック */
  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo): void => {
      const accidentImage: AccidentImage = {
        ...editState.vehicleStateInfo.accidentImage,
      }
      accidentImage[name] = imageInfo
      setVehicleStateInfo({
        accidentImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  /** 写真以外スイッチ変更時のコールバック */
  const handleNoPhotoChanged = React.useCallback(
    (value: boolean): void => {
      setVehicleStateInfo({
        noPhoto: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 破損個所情報変更時のコールバック */
  const handleDamagedPartChanged = React.useCallback(
    (value: AccidentPartInfo): void => {
      setVehicleStateInfo({
        accidentPart: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** バイクでのパンクトラブルスイッチ変更時のコールバック */
  const handleIsMotorbikeChanged = React.useCallback(
    (value: boolean): void => {
      setVehicleStateInfo({
        isMotorbike: value,
        accidentPart: {
          ...initialQnEditState.vehicleStateInfo.accidentPart,
        },
      })
    },
    [setVehicleStateInfo]
  )

  /** タイヤ軸が複数ある車両トラブルのスイッチ変更時のコールバック */
  const handleIsNotApplicablePartsChanged = React.useCallback(
    (value: boolean): void => {
      setVehicleStateInfo({
        isNotApplicableParts: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 落輪ステータス選択変更時のコールバック */
  const handleFallingWheelStatusChanged = React.useCallback(
    (value: FallingWheelStatusTypes): void => {
      setVehicleStateInfo({
        fallingWheelStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 現場の種類選択変更時のコールバック */
  const handleFieldTypeChanged = React.useCallback(
    (value: FieldTypes): void => {
      setVehicleStateInfo({
        fieldType: {
          ...fieldType,
          type: value,
        },
      })
    },
    [setVehicleStateInfo]
  )

  /** 現場の種類のその他テキスト入力変更時のコールバック */
  const handleFieldTypeTextChanged = React.useCallback(
    (freeText: string): void => {
      setVehicleStateInfo({
        fieldType: {
          ...fieldType,
          others: freeText,
        },
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])
  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  return (
    <RequestTemplate
      title={'落輪・乗り上げ'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedImageGroup
            accidentImage={accidentImage}
            title={`${VEHICLE_STATE_FORM.accidentImage.formName}を2枚撮影`}
            isRequired={!noPhoto}
            onChanged={handleImageChanged}
          />
          <SwitchButtonGroup
            title={`${VEHICLE_STATE_FORM.noPhoto.formName}は選択`}
            isSwitched={noPhoto}
            label={`${VEHICLE_STATE_FORM.noPhoto.label}`}
            onChanged={handleNoPhotoChanged}
          />
          <PhotoPointNote />
          {noPhoto && (
            <>
              <SelectDamagedPartGroup
                title={damagedAreaTitle}
                isRequired={!isNotApplicableParts}
                damagedStatus={{
                  parts: accidentPart.parts,
                  existsDamagedTire: accidentPart.existsDamagedTire,
                }}
                existsDamagedTireLabel={`${VEHICLE_STATE_FORM.accidentPart.label}`}
                isMotorbike={isMotorbike}
                onChanged={(value: AccidentPartInfo): void => {
                  handleDamagedPartChanged(value)
                }}
              />
              {(isSjDomain() || isKsDomain()) && (
                <>
                  <SwitchButtonGroup
                    title={motorbikeTitle}
                    isSwitched={isMotorbike}
                    label={motorbikeLabel}
                    onChanged={handleIsMotorbikeChanged}
                  />
                  <SwitchButtonGroup
                    title={`${VEHICLE_STATE_FORM.notApplicableParts.formName}`}
                    isSwitched={isNotApplicableParts}
                    label={`${VEHICLE_STATE_FORM.notApplicableParts.label}`}
                    onChanged={handleIsNotApplicablePartsChanged}
                  />
                </>
              )}
            </>
          )}
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.fallingWheelStatus.formName}を選択`}
            isRequired
            options={fallingWheelStatusOptions}
            selectedValue={fallingWheelStatus}
            onValueChanged={handleFallingWheelStatusChanged}
          />
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.fieldType.formName}を選択`}
            isRequired
            options={fieldTypeOptions}
            needFreeText={fieldType.type.includes(FieldTypes.others)}
            freeText={fieldType.others}
            freeMaxLength={100}
            selectedValue={fieldType.type}
            onValueChanged={handleFieldTypeChanged}
            onTextChanged={handleFieldTypeTextChanged}
          />
          {isKsDomain() ? (
            <KsSelectVehicleModificationContent />
          ) : (
            <SelectVehicleModificationContent />
          )}
          {isKsDomain() ? (
            <KsSelectDestinationContent isIntroduction={true} />
          ) : (
            <SelectDestinationContent />
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      isDisabled={isDisabled}
      notClearEditingItem
      error={errorMessage}
    />
  )
}

export default FallingWheel
